import { AsterMark, Button, StatusPill } from '@head-of-trading/ui';
import type { AccountView } from '@head-of-trading/contracts';
import type { MissionCatalogItem } from '../lib/api.js';
import { LanguageSwitcher, useLanguage } from '../i18n/context.js';
import { screensCopy } from '../i18n/screens.js';
import { formatInteger } from '../lib/format.js';

interface MissionHubProps {
  readonly account: AccountView;
  readonly missions: readonly MissionCatalogItem[];
  readonly busy: boolean;
  readonly tooSmall: boolean;
  readonly onSelect: (mission: MissionCatalogItem) => void;
  readonly onResume: () => void;
  readonly onAbandon: () => void;
  readonly onHistory: () => void;
  readonly onChangeAccount: () => void;
}

export function MissionHub({ account, missions, busy, tooSmall, onSelect, onResume, onAbandon, onHistory, onChangeAccount }: MissionHubProps) {
  const { language } = useLanguage();
  const copy = screensCopy[language].missionHub;
  const hasActive = account.hasActiveAttempt && account.activeAttemptId;
  return (
    <main className="hub">
      <header className="hub-header">
        <AsterMark className="hub-header__brand" />
        <nav aria-label={copy.accountNavigation}><span>{copy.trader} · <strong>{account.displayName}</strong></span><Button data-testid="history-link" variant="quiet" onClick={onHistory}>{copy.history}</Button><Button variant="quiet" onClick={onChangeAccount}>{copy.changePseudonym}</Button><LanguageSwitcher /></nav>
      </header>
      <section className="hub-hero">
        <p className="eyebrow">{copy.eyebrow}</p>
        <h1>{copy.title}</h1>
        <p>{copy.description}</p>
      </section>
      {tooSmall && <div className="viewport-warning" role="status"><strong>{copy.viewportTitle}</strong><span>{copy.viewportDescription}</span></div>}
      {hasActive && (
        <aside className="active-attempt" aria-label={copy.activeAttemptLabel}>
          <div><span className="live-dot" /> <strong>{copy.activeAttemptTitle}</strong><p>{copy.activeAttemptDescription}</p></div>
          <div><Button variant="quiet" disabled={busy} onClick={onAbandon}>{copy.abandon}</Button><Button variant="primary" disabled={busy || tooSmall} onClick={onResume}>{copy.resume}</Button></div>
        </aside>
      )}
      <section data-testid="mission-grid" className="mission-grid" aria-label={copy.availableMissions}>
        {missions.map((mission, index) => (
          <article data-testid={`mission-card-${mission.missionId}`} className={`mission-card mission-card--${mission.missionId.toLowerCase()}`} key={mission.missionId}>
            <div className="mission-card__number">0{index + 1}</div>
            <div className="mission-card__head"><span>{mission.missionId}</span>{mission.recommended && <StatusPill tone="info">{copy.recommended}</StatusPill>}</div>
            <h2>{mission.title}</h2>
            <p>{mission.hook}</p>
            <dl><div><dt>{copy.duration}</dt><dd>{mission.durationLabel}</dd></div><div><dt>{copy.status}</dt><dd>{mission.played ? copy.alreadyPlayed : copy.notPlayed}</dd></div></dl>
            <div className="mission-skills">{mission.skills.map((skill) => <span key={skill}>{skill}</span>)}</div>
            <div className="mission-score"><span>{copy.latest} <strong>{mission.latestScore === null ? copy.notAvailable : formatInteger(mission.latestScore, language)}</strong></span><span>{copy.best} <strong>{mission.bestScore === null ? copy.notAvailable : formatInteger(mission.bestScore, language)}</strong></span></div>
            <Button data-testid={`mission-start-${mission.missionId}`} variant="primary" size="lg" disabled={Boolean(hasActive) || tooSmall || !mission.brief} onClick={() => onSelect(mission)}>{copy.openBrief}</Button>
            {!mission.brief && <small className="form-error">{copy.briefUnavailable}</small>}
          </article>
        ))}
      </section>
    </main>
  );
}
